import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent } from '@/components/ui/card';

// Fix for default markers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface SOSReport {
  id: string;
  user_id: string;
  type: 'lost' | 'found' | 'emergency' | 'danger';
  title: string;
  description: string | null;
  animal_species: string | null;
  animal_breed: string | null;
  animal_name: string | null;
  image_url: string | null;
  latitude: number;
  longitude: number;
  location_precision: 'precise' | 'street' | 'approximate';
  status: 'open' | 'spotted' | 'in_progress' | 'resolved' | 'cancelled';
  contact_phone: string | null;
  allow_public_sharing: boolean;
  last_spotted_at: string | null;
  helper_count: number;
  created_at: string;
  updated_at: string;
  profiles?: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
}

interface SOSMapProps {
  reports: SOSReport[];
  userLocation: { lat: number; lng: number } | null;
  onReportClick: (report: SOSReport) => void;
}

export const SOSMap = ({ reports, userLocation, onReportClick }: SOSMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const markersLayer = useRef<L.LayerGroup | null>(null);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'lost': return '🐕';
      case 'found': return '🔍';
      case 'emergency': return '🚨';
      case 'danger': return '⚠️';
      default: return '📍';
    }
  };

  const getTypeColor = (type: string, status: string) => {
    if (status === 'resolved') return '#10b981'; // green
    if (status === 'cancelled') return '#6b7280'; // gray
    
    switch (type) {
      case 'lost': return '#ef4444'; // red
      case 'found': return '#3b82f6'; // blue  
      case 'emergency': return '#dc2626'; // dark red
      case 'danger': return '#f59e0b'; // orange
      default: return '#8b5cf6'; // purple
    }
  };

  const createCustomIcon = (report: SOSReport) => {
    const color = getTypeColor(report.type, report.status);
    const icon = getTypeIcon(report.type);
    
    return L.divIcon({
      html: `
        <div style="
          background-color: ${color};
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 3px solid white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          cursor: pointer;
        ">
          ${icon}
        </div>
      `,
      className: 'custom-div-icon',
      iconSize: [36, 36],
      iconAnchor: [18, 18],
      popupAnchor: [0, -18]
    });
  };

  useEffect(() => {
    if (!mapContainer.current) return;

    // Initialize map
    if (!map.current) {
      map.current = L.map(mapContainer.current, {
        center: userLocation ? [userLocation.lat, userLocation.lng] : [41.9028, 12.4964], // Rome default
        zoom: userLocation ? 14 : 10,
        zoomControl: true,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(map.current);

      markersLayer.current = L.layerGroup().addTo(map.current);
    }

    // Clear existing markers
    if (markersLayer.current) {
      markersLayer.current.clearLayers();
    }

    // Add user location marker
    if (userLocation && markersLayer.current) {
      const userMarker = L.marker([userLocation.lat, userLocation.lng], {
        icon: L.divIcon({
          html: `
            <div style="
              background-color: #3b82f6;
              width: 20px;
              height: 20px;
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            "></div>
          `,
          className: 'user-location-icon',
          iconSize: [20, 20],
          iconAnchor: [10, 10]
        })
      });
      
      userMarker.bindPopup('La tua posizione').addTo(markersLayer.current);
    }

    // Add SOS markers - only show open reports
    reports.filter(report => report.status === 'open').forEach((report) => {
      if (!markersLayer.current) return;

      const marker = L.marker(
        [report.latitude, report.longitude], 
        { icon: createCustomIcon(report) }
      );

      const helperCount = Number(report.helper_count) || 0;
      const popupContent = `
        <div style="min-width: 200px;">
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
            <span style="font-size: 18px;">${getTypeIcon(report.type)}</span>
            <strong style="font-size: 14px;">${report.title}</strong>
          </div>
          ${report.animal_species ? `<div style="margin-bottom: 4px; font-size: 12px; color: #666;">🐾 ${report.animal_species}${report.animal_breed ? ` (${report.animal_breed})` : ''}</div>` : ''}
          ${report.description ? `<div style="margin-bottom: 8px; font-size: 12px;">${report.description.substring(0, 100)}${report.description.length > 100 ? '...' : ''}</div>` : ''}
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: #888;">
            <span>👥 ${helperCount} ${helperCount === 1 ? 'aiuta' : 'aiutano'}</span>
            <span>${new Date(report.created_at).toLocaleDateString('it-IT')}</span>
          </div>
        </div>
      `;

      marker.bindPopup(popupContent);
      marker.on('click', () => onReportClick(report));
      marker.addTo(markersLayer.current);
    });

    // Adjust map view to show all markers
    if (reports.length > 0 && map.current) {
      const group = new L.FeatureGroup(
        reports.map(report => 
          L.marker([report.latitude, report.longitude])
        )
      );
      
      if (userLocation) {
        group.addLayer(L.marker([userLocation.lat, userLocation.lng]));
      }
      
      map.current.fitBounds(group.getBounds().pad(0.1));
    }

    // Cleanup function
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
        markersLayer.current = null;
      }
    };
  }, [reports, userLocation, onReportClick]);

  return (
    <div className="space-y-4">
      <div 
        ref={mapContainer} 
        className="w-full h-[400px] md:h-[500px] rounded-lg border border-border shadow-sm relative z-0"
        style={{ zIndex: 0 }}
      />
      
      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3 text-sm">Legenda</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center text-white">
                🐕
              </div>
              <span>Smarriti</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white">
                🔍
              </div>
              <span>Trovati</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center text-white">
                🚨
              </div>
              <span>Emergenze</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center text-white">
                ⚠️
              </div>
              <span>Pericoli</span>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center gap-2 text-xs">
              <div className="w-4 h-4 rounded-full bg-blue-500"></div>
              <span>La tua posizione</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};